import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  RefreshCw, 
  Download, 
  TrendingUp, 
  BarChart3, 
  PieChart, 
  Users,
  Mail,
  Clock,
  AlertCircle,
  CheckCircle,
  Plus
} from 'lucide-react';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { getApiBaseUrl } from '../config/env';

interface AIDailyReport {
  id: number;
  report_date: string;
  total_emails_processed: number;
  user_emails_count: number;
  categories_summary: Record<string, number>;
  sentiment_summary: Record<string, number>;
  priority_summary: Record<string, number>;
  summary_text: string;
  key_insights: string[];
  recommendations: string[];
  trends: Record<string, any>;
  created_at: string;
}

const AIDailyReportsPage: React.FC = () => {
  const [reports, setReports] = useState<AIDailyReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AIDailyReport | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end,
        limit: '30'
      });
      
      const response = await fetch(`${getApiBaseUrl()}/ai/daily-reports?${params}`);
      if (response.ok) {
        const data = await response.json();
        setReports(data);
        if (data.length > 0 && !selectedReport) {
          setSelectedReport(data[0]);
        }
      }
    } catch (error) {
      console.error('获取AI日报失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/ai/daily-reports/generate`, {
        method: 'POST',
      });

      if (response.ok) {
        alert('日报生成任务已创建，请稍后刷新查看结果');
        setTimeout(fetchReports, 3000);
      }
    } catch (error) {
      console.error('生成日报失败:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    let date: Date;
    if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      date = new Date(dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
  };

  const getSentimentColor = (sentiment: string) => {
    const colorMap: Record<string, string> = {
      '积极': 'text-green-600',
      '中性': 'text-gray-600',
      '消极': 'text-red-600',
      '愤怒': 'text-red-800'
    };
    return colorMap[sentiment] || 'text-gray-600';
  };

  const getPriorityColor = (priority: string) => {
    const colorMap: Record<string, string> = {
      '高优先级': 'text-red-600',
      '中优先级': 'text-yellow-600',
      '低优先级': 'text-green-600'
    };
    return colorMap[priority] || 'text-gray-600';
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* 页面头部 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI日报管理</h1>
            <p className="text-gray-600 mt-1">查看和管理AI生成的每日邮件分析报告</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">开始日期:</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">结束日期:</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              />
            </div>
            <Button onClick={fetchReports} loading={loading} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              刷新
            </Button>
            <Button onClick={generateReport}>
              <Plus className="w-4 h-4 mr-2" />
              生成今日报告
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 报告列表 */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">日报列表</h2>
                <p className="text-sm text-gray-600">共 {reports.length} 份报告</p>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {reports.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>暂无日报数据</p>
                    <p className="text-sm mt-1">点击"生成今日报告"创建新报告</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {reports.map((report) => (
                      <div
                        key={report.id}
                        onClick={() => setSelectedReport(report)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 ${
                          selectedReport?.id === report.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-gray-900">
                            {formatDate(report.report_date)}
                          </h3>
                          <Badge variant="info" className="text-xs">
                            {report.user_emails_count} 封邮件
                          </Badge>
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {report.total_emails_processed}
                            </span>
                            <span className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {formatDateTime(report.created_at).split(' ')[1]}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 报告详情 */}
          <div className="lg:col-span-2">
            {selectedReport ? (
              <div className="space-y-6">
                {/* 报告概览 */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900">
                      {formatDate(selectedReport.report_date)} 日报
                    </h2>
                    <div className="flex items-center space-x-2">
                      <Badge variant="success">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        已生成
                      </Badge>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-1" />
                        导出
                      </Button>
                    </div>
                  </div>

                  {/* 统计卡片 */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <Mail className="w-6 h-6 text-blue-500" />
                        <div className="ml-3">
                          <p className="text-sm text-blue-600">总邮件数</p>
                          <p className="text-lg font-bold text-blue-900">
                            {selectedReport.total_emails_processed}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <Users className="w-6 h-6 text-green-500" />
                        <div className="ml-3">
                          <p className="text-sm text-green-600">用户邮件</p>
                          <p className="text-lg font-bold text-green-900">
                            {selectedReport.user_emails_count}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <BarChart3 className="w-6 h-6 text-purple-500" />
                        <div className="ml-3">
                          <p className="text-sm text-purple-600">分类数</p>
                          <p className="text-lg font-bold text-purple-900">
                            {Object.keys(selectedReport.categories_summary).length}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <TrendingUp className="w-6 h-6 text-orange-500" />
                        <div className="ml-3">
                          <p className="text-sm text-orange-600">洞察数</p>
                          <p className="text-lg font-bold text-orange-900">
                            {selectedReport.key_insights.length}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 总结文本 */}
                  {selectedReport.summary_text && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">日报总结</h3>
                      <p className="text-gray-700 whitespace-pre-line">
                        {selectedReport.summary_text}
                      </p>
                    </div>
                  )}
                </div>

                {/* 分类统计 */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">邮件分类统计</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-3">分类分布</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedReport.categories_summary).map(([category, count]) => (
                          <div key={category} className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">{category}</span>
                            <div className="flex items-center space-x-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-500 h-2 rounded-full"
                                  style={{
                                    width: `${(count / selectedReport.user_emails_count) * 100}%`
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-900 w-8">
                                {count}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="font-medium text-gray-700 mb-3">情感分析</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedReport.sentiment_summary).map(([sentiment, count]) => (
                          <div key={sentiment} className="flex items-center justify-between">
                            <span className={`text-sm ${getSentimentColor(sentiment)}`}>
                              {sentiment}
                            </span>
                            <div className="flex items-center space-x-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{
                                    width: `${(count / selectedReport.user_emails_count) * 100}%`
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium text-gray-900 w-8">
                                {count}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 优先级分布 */}
                  <div className="mt-6">
                    <h4 className="font-medium text-gray-700 mb-3">优先级分布</h4>
                    <div className="grid grid-cols-3 gap-4">
                      {Object.entries(selectedReport.priority_summary).map(([priority, count]) => (
                        <div key={priority} className="text-center">
                          <div className={`text-2xl font-bold ${getPriorityColor(priority)}`}>
                            {count}
                          </div>
                          <div className="text-sm text-gray-600">{priority}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 关键洞察和建议 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 关键洞察 */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <AlertCircle className="w-5 h-5 mr-2 text-blue-500" />
                      关键洞察
                    </h3>
                    {selectedReport.key_insights.length > 0 ? (
                      <ul className="space-y-2">
                        {selectedReport.key_insights.map((insight, index) => (
                          <li key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{insight}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500 text-sm">暂无关键洞察</p>
                    )}
                  </div>

                  {/* 建议 */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                      改进建议
                    </h3>
                    {selectedReport.recommendations.length > 0 ? (
                      <ul className="space-y-2">
                        {selectedReport.recommendations.map((recommendation, index) => (
                          <li key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{recommendation}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-gray-500 text-sm">暂无改进建议</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-12 text-center">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">选择日报查看详情</h3>
                <p className="text-gray-600">从左侧列表中选择一份日报来查看详细内容</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIDailyReportsPage; 